import React, { useState } from 'react'; // 1. Imported useState
import { defineMessages } from 'react-intl';
import { QrcodeOutlined } from '@ant-design/icons';
import QrCodeModal from '@components/common/form/QrCodeModal';
import ListPage from '@components/common/layout/ListPage';
import PageWrapper from '@components/common/layout/PageWrapper';
import { DEFAULT_FORMAT, DEFAULT_TABLE_ITEM_SIZE, ORDER_STATE_DONE, SERVICE_DELIVERY } from '@constants';
import apiConfig from '@constants/apiConfig';
import { FieldTypes } from '@constants/formConfig';
import { orderKindOptions, orderStateOptions, paymentMethodOptions } from '@constants/masterData';
import useFetch from '@hooks/useFetch';
import useListModal from '@hooks/useListModal';
import useRestaurant from '@hooks/useRestaurant';
import useTranslate from '@hooks/useTranslate';
import { BaseTable, BaseTooltip } from '@itz/react-cms-element';
import { commonMessage } from '@locales/intl';
import { showErrorMessage } from '@services/notifyService';
import { convertUtcToLocalTime, formatMoney } from '@utils';
import { Button, Tag } from 'antd';

const message = defineMessages({
    objectName: 'Order',
});

const OrderListPage = () => {
    const translate = useTranslate();
    const { currentSettingParse } = useRestaurant();
    const stateValue = translate.formatKeys(orderStateOptions, ['label']);
    const paymentMethodValue = translate.formatKeys(paymentMethodOptions, ['label']);

    const [qrModalVisible, setQrModalVisible] = useState(false);
    const [qrCodeUrl, setQrCodeUrl] = useState('');

    const { execute, loading: qrLoading } = useFetch(apiConfig.order.requestDeliveryQrCode, { immediate: false });

    const handleGetQrCode = (record) => {
        execute({
            params: { orderId: record.id },
            onCompleted: (response) => {
                if (response.result === true) {
                    const qrCode = response.data;
                    setQrCodeUrl(qrCode);
                    setQrModalVisible(true);
                }
            },
            onError: (error) => {
                const message = error.response?.data?.message || 'Failed to get QR code';
                showErrorMessage(message, translate);
            },
        });
    };

    const {
        data,
        mixinFuncs,
        queryFilter,
        loading,
        pagination,
    } = useListModal({
        apiConfig: apiConfig.order,
        options: {
            pageSize: DEFAULT_TABLE_ITEM_SIZE,
            objectName: translate.formatMessage(message.objectName),
        },
        override: (funcs) => {
            funcs.mappingData = (response) => {
                if (response.result === true) {
                    return {
                        data: response.data.content,
                        total: response.data.totalElements,
                    };
                }
            };
            funcs.additionalActionColumnButtons = () => ({
                qrCode: (record) => {
                    // const hasPerm = funcs.hasPermission([apiConfig.driver.delete.baseURL]);
                    const hasPerm = funcs.hasPermission([apiConfig.driver.delete.baseURL]) && record.state === ORDER_STATE_DONE && record.kind === SERVICE_DELIVERY;
                    return (
                        <BaseTooltip title={translate.formatMessage(commonMessage.qrCode)}>
                            <Button
                                type="link"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleGetQrCode(record);
                                }}
                                disabled={!hasPerm || qrLoading}
                                style={{ padding: 0 }}
                            >
                                <QrcodeOutlined style={{ color: !hasPerm ? 'gray' : 'blue' }} />
                            </Button>
                        </BaseTooltip>
                    );
                },
            });
        },
    });

    const columns = [
        {
            title: translate.formatMessage(commonMessage.createdDate),
            dataIndex: 'createdDate',
            width: '180px',
            render: (createdDate) => {
                const createdDateLocal = convertUtcToLocalTime(createdDate, DEFAULT_FORMAT, DEFAULT_FORMAT);
                return <div>{createdDateLocal}</div>;
            },
        },
        {
            title: translate.formatMessage(commonMessage.orderStt),
            dataIndex: 'orderStt',
            width: '140px',
        },
        {
            title: translate.formatMessage(commonMessage.customerName),
            dataIndex: ['customer', 'userName'],
        },
        {
            title: translate.formatMessage(commonMessage.deliveryFee),
            dataIndex: 'shipMoney',
            width: '180px',
            align: 'center',
            render: (shipMoney) => {
                return formatMoney(shipMoney || 0, currentSettingParse);
            },
        },
        {
            title: translate.formatMessage(commonMessage.discountAmount),
            dataIndex: ['voucher', 'discountAmount'],
            width: '180px',
            align: 'center',
            render: (discountAmount) => {
                return formatMoney(discountAmount || 0, currentSettingParse);
            },
        },
        {
            title: translate.formatMessage(commonMessage.totalMoney),
            dataIndex: 'totalMoney',
            width: '180px',
            align: 'center',
            render: (totalMoney, record) => {
                const shipMoney = record.shipMoney || 0;
                const voucherDiscount = record?.voucher?.discountAmount || 0;
                const total = (totalMoney / 100) + shipMoney - voucherDiscount;
                return formatMoney(total, currentSettingParse);
            },
        },
        {
            title: translate.formatMessage(commonMessage.orderType),
            dataIndex: 'kind',
            width: '180px',
            render: (kind) => {
                const currentState = orderKindOptions?.find(item => item.value === kind);

                return currentState?.label;
            },
        },
        {
            title: translate.formatMessage(commonMessage.payment),
            dataIndex: 'paymentMethod',
            width: '200px',
            render: (paymentMethod) => {
                const currentPayment = paymentMethodValue?.find(item => item.value === paymentMethod);
                return <span>{currentPayment?.label}</span>;
            },
        },
        {
            title: translate.formatMessage(commonMessage.state),
            dataIndex: 'state',
            width: '120px',
            align: 'center',
            render: (state) => {
                const currentState = stateValue?.find(item => item.value === state);

                return (
                    <Tag color={currentState?.color}>
                        <div style={{ padding: '0 4px', fontSize: 14 }}>{currentState?.label}</div>
                    </Tag>
                );
            },
        },
        // mixinFuncs.renderActionColumn(
        //     {
        //         qrCode: true,
        //     },
        //     { width: '120px' },
        // ),
    ];

    const searchFields = [
        {
            key: 'kind',
            placeholder: translate.formatMessage(commonMessage.orderType),
            type: FieldTypes.SELECT,
            options: orderKindOptions,
            submitOnChanged: true,
            colSpan: 5,
        },
        {
            key: 'state',
            placeholder: translate.formatMessage(commonMessage.state),
            type: FieldTypes.SELECT,
            options: stateValue,
            submitOnChanged: true,
            colSpan: 5,
        },
    ];

    return (
        <PageWrapper routes={[{ breadcrumbName: translate.formatMessage(message.objectName) }]}>
            <ListPage
                searchForm={mixinFuncs.renderSearchForm({ fields: searchFields, initialValues: queryFilter })}
                baseTable={
                    <BaseTable
                        onChange={mixinFuncs.changePagination}
                        columns={columns}
                        dataSource={data}
                        pagination={pagination}
                        style={{ cursor: 'pointer' }}
                        loading={loading}
                    />
                }
            />

            <QrCodeModal
                open={qrModalVisible}
                onClose={() => setQrModalVisible(false)}
                qrCodeUrl={qrCodeUrl}
            />
        </PageWrapper>
    );
};

export default OrderListPage;